import React from 'react';
import { Box, Button, Card, Typography } from '@mui/material';
import { statusTone, type StatusTone } from '../../theme';
import PageContainer from '../ui/PageContainer';

export interface ResultAction {
  label: string;
  onClick: () => void;
}

interface PaymentResultPanelProps {
  tone: StatusTone;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  /** The one contained button. */
  primaryAction?: ResultAction;
  /** A quieter text button. */
  secondaryAction?: ResultAction;
  /** Extra content under the description, e.g. a progress bar. */
  children?: React.ReactNode;
}

/**
 * The outcome of a checkout: a tinted icon tile, a sentence-case title, one
 * line on what it means, and at most one primary and one text button. Shared
 * by the payment success and cancel pages.
 */
const PaymentResultPanel: React.FC<PaymentResultPanelProps> = ({
  tone,
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  children,
}) => {
  const colors = statusTone[tone];
  return (
    <PageContainer width="narrow">
      <Card
        data-testid="payment-result"
        data-tone={tone}
        sx={{ maxWidth: 560, mx: 'auto', mt: { xs: 1, md: 4 }, px: { xs: 3, sm: 5 }, py: { xs: 4, sm: 5 }, textAlign: 'center' }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2.5,
            borderRadius: 3,
            display: 'grid',
            placeItems: 'center',
            backgroundColor: colors.bg,
            color: colors.fg,
            '& svg': { fontSize: 28 },
          }}
        >
          {icon}
        </Box>
        <Box role="status" aria-live="polite">
          <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 440, mx: 'auto' }}>
            {description}
          </Typography>
        </Box>
        {children}
        {(primaryAction || secondaryAction) && (
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mt: 3.5 }}>
            {primaryAction && (
              <Button variant="contained" onClick={primaryAction.onClick}>
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button variant="text" onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </Button>
            )}
          </Box>
        )}
      </Card>
    </PageContainer>
  );
};

export default PaymentResultPanel;
